<script lang="ts">
    import type { IconType } from "@components/material/Icon.module"
    import Icon from "@components/material/Icon.svelte"
    import Switch from "@components/material/Switch.svelte"
    import { twMerge } from "tailwind-merge"
    import NotificationMiniSettings from "./NotificationMiniSettings.svelte"
    import { isGenerator, timeCrunchMachine } from "@utils/practice/reminder-time-conversion"
    import type { PracticeAny } from "@circleofa/acim-engine"
    import ButtonGroup from "./slider/button-group.svelte"
    import { ReminderMemoryManagerSettings } from "@utils/practice/reminder-memory-manager"
    import { Channel } from "@utils/cap-notification-manager/cap-notification-manager"
    import ReminderDisabledPopup from "./ReminderDisabledPopup.svelte"
    export let disabled = false

    export let channel: Channel
    export let practice: PracticeAny | undefined = undefined
    export let generator: ReturnType<typeof timeCrunchMachine> | undefined = undefined

    /**
     * Pass down reactive value
     */
    export let reactiveMemoryManager: ReminderMemoryManagerSettings

    export let className: string
    export { className as class }
    export let icon: IconType
    $: on = reactiveMemoryManager.channelIsEnabled(channel)
    export let settingsLink: string | undefined = undefined
    export let settingsText: string | undefined = undefined

    // If there's no practice, it's because it's tomorrows lesson reminder
    $: title = practice ? practice.type : "Tomorrow's lesson"
    $: body = practice
        ? practice.sentence
        : "Receive a morning prompt when your next Workbook lesson is ready."
</script>

<div class="relative">
    <div
        class={twMerge(
            disabled ? "pointer-events-none opacity-60 relative" : "",
            "themed-box p-4 border-l-4",
            className,
        )}
    >
        {#if !disabled}
            <Switch
                on:off={() => reactiveMemoryManager.setChannel(channel, false)}
                on:on={() => reactiveMemoryManager.setChannel(channel, true)}
                class={"float-right mb-2 ml-2 "}
                toggled={disabled ? false : on}
                {disabled}
            />
        {/if}
        <h3 class="reminder-editor-title">
            <Icon {icon} class="w-6 mr-2 inline-block align-bottom" />{title}
            {#if settingsLink}
                <span class="text-sm">
                    <NotificationMiniSettings href={settingsLink}
                        >{settingsText}</NotificationMiniSettings
                    >
                </span>
            {/if}
        </h3>
        <p class="reminder-editor-copy">
            {body}
            {#if isGenerator(generator)}
                <ButtonGroup
                    list={generator.options}
                    selected={reactiveMemoryManager.getGeneration(generator)}
                    select={(selection) =>
                        reactiveMemoryManager.updateGeneratorOption(generator, selection)}
                />
            {/if}
        </p>
    </div>
    {#if disabled}
        <ReminderDisabledPopup
            reason={channel === Channel.TEMPTATION ? "temptations" : "ios-frequent"}
        />
    {/if}
</div>

<style>
    .reminder-editor-title {
        margin: 0 0 0.35rem;
        color: var(--color-purple-900);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.05rem;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 1.3;
    }

    .reminder-editor-copy {
        margin: 0;
        color: var(--color-grey-600);
        font-size: 0.9rem;
        line-height: 1.65;
    }

    :global(.darkmode) .reminder-editor-title {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .reminder-editor-copy {
        color: var(--color-grey-200);
    }
</style>
